import { useContext, useState } from 'react';
import {
    Modal, ModalDialog, ModalOverflow, ModalClose,
    Typography, Stack, Box, Button, Alert,
} from '@mui/joy';
import { Snowflake } from '@phosphor-icons/react';

import { LayoutContext } from '../../context';
import { APICreateGrandSlamInvoice, APICreateTgPromoInvoice, APICreateProdamusInvoice, APICreateQbankInvoice } from '../../api';

// ─── Plan data ──────────────────────────────────────────────────────────────
// Sync points: server/subscriptions/views.py (TG_PROMO_PLANS, GRAND_SLAM_PLANS,
// GRAND_SLAM_PROMO_PLANS) and react_site/src/data/grand-slam-content.js.

const GRAND_SLAM_PLANS = [
    { period: 1,  price: '4 900 ₽',  oldPrice: '9 000 ₽',  perMonth: '4 900 ₽ / мес' },
    { period: 3,  price: '12 900 ₽', oldPrice: '19 500 ₽', perMonth: '4 300 ₽ / мес' },
    { period: 6,  price: '23 900 ₽', oldPrice: '39 000 ₽', perMonth: '3 983 ₽ / мес' },
    { period: 12, price: '24 900 ₽', oldPrice: '49 900 ₽', perMonth: '2 075 ₽ / мес', featured: true },
];

// TG promo QBank (1590/3990/6990/12990) — sync с TG_PROMO_PLANS на backend.
const QBANK_PLANS = [
    { period: 1,  price: '1 590 ₽',  oldPrice: '2 500 ₽',  perMonth: '1 590 ₽ / мес' },
    { period: 3,  price: '3 990 ₽',  oldPrice: '4 990 ₽',  perMonth: '1 330 ₽ / мес' },
    { period: 6,  price: '6 990 ₽',  oldPrice: '8 990 ₽',  perMonth: '1 165 ₽ / мес' },
    { period: 12, price: '12 990 ₽', oldPrice: '17 990 ₽', perMonth: '1 082 ₽ / мес' },
];

// Public QBank only (для не-TG юзеров). Sync с QBANK_PUBLIC_PLANS на backend.
// 1m без скидки = anchor, остальные по убывающей.
const QBANK_PUBLIC_PLANS = [
    { period: 1,  price: '2 490 ₽',  oldPrice: '4 900 ₽',  perMonth: '2 490 ₽ / мес' },
    { period: 3,  price: '4 990 ₽',  oldPrice: '7 470 ₽',  perMonth: '1 663 ₽ / мес' },
    { period: 6,  price: '8 990 ₽',  oldPrice: '14 940 ₽', perMonth: '1 498 ₽ / мес' },
    { period: 12, price: '16 990 ₽', oldPrice: '29 880 ₽', perMonth: '1 416 ₽ / мес' },
];

const PRO_PROMO_PLANS = [
    { period: 1,  price: '3 490 ₽',  oldPrice: '4 900 ₽',  perMonth: '3 490 ₽ / мес' },
    { period: 3,  price: '8 990 ₽',  oldPrice: '12 900 ₽', perMonth: '2 997 ₽ / мес' },
    { period: 6,  price: '15 900 ₽', oldPrice: '23 900 ₽', perMonth: '2 650 ₽ / мес' },
    { period: 12, price: '24 900 ₽', oldPrice: '29 900 ₽', perMonth: '2 075 ₽ / мес', hasFreeze: true },
];

const PERIODS = [
    { value: 1,  short: '1 мес', long: 'Месяц' },
    { value: 3,  short: '3 мес', long: 'Квартал' },
    { value: 6,  short: '6 мес', long: 'Полгода' },
    { value: 12, short: '12 мес', long: 'Год' },
];

const PRO_FEATURES = [
    { in: true, label: '6 000+ мини-тестов' },
    { in: true, label: '480+ клинических задач' },
    { in: true, label: '1 200+ модулей из First Aid' },
    { in: true, label: '100+ видео-лекций' },
    { in: true, label: 'Учебные планы и гайды' },
];

const QBANK_FEATURES = [
    { in: true,  label: '6 000+ мини-тестов' },
    { in: true,  label: '480+ клинических задач' },
    { in: false, label: '1 200+ модулей из First Aid' },
    { in: false, label: '100+ видео-лекций' },
    { in: false, label: 'Учебные планы и гайды' },
];

// ─── Design tokens (editorial, medical journal) ─────────────────────────────
const D = {
    bg:            '#FAF7F2',
    bgCard:        '#FFFFFF',
    bgRecommended: '#F2EBDD',
    ink:           '#1F1B16',
    inkSecondary:  '#5C544A',
    inkTertiary:   '#9B9384',
    rule:          '#D8D0C2',
    accent:        '#B84C31',
    success:       '#3E6B57',
    serif:         '"Fraunces", "Iowan Old Style", Georgia, serif',
    tabular:       { fontVariantNumeric: 'tabular-nums' },
    eyebrow: {
        fontSize: '0.66rem',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        fontWeight: 600,
    },
};

// Subtle paper-grain — adds warmth, doesn't dominate.
const grainBg = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.04 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`;

// ─── PeriodSelector ─────────────────────────────────────────────────────────
function PeriodSelector({ selected, onChange }) {
    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            borderTop: `0.5px solid ${D.rule}`,
            borderBottom: `0.5px solid ${D.rule}`,
            mb: 2.5,
        }}>
            {PERIODS.map((p, i) => {
                const active = p.value === selected;
                return (
                    <Box
                        key={p.value}
                        onClick={() => onChange(p.value)}
                        sx={{
                            cursor: 'pointer',
                            flex: 1,
                            py: 1.25,
                            position: 'relative',
                            textAlign: 'center',
                            borderLeft: i === 0 ? 'none' : `0.5px solid ${D.rule}`,
                            transition: 'background-color 0.2s',
                            '&:hover': { bgcolor: 'rgba(184,76,49,0.04)' },
                        }}
                    >
                        <Box sx={{
                            ...D.eyebrow,
                            color: active ? D.ink : D.inkTertiary,
                            fontSize: '0.62rem',
                            mb: 0.25,
                        }}>
                            {p.short}
                        </Box>
                        <Typography sx={{
                            fontFamily: D.serif,
                            fontSize: '1.05rem',
                            fontWeight: 400,
                            color: active ? D.ink : D.inkTertiary,
                            lineHeight: 1,
                            transition: 'color 0.2s',
                        }}>
                            {p.long}
                        </Typography>
                        {active && (
                            <Box sx={{
                                position: 'absolute',
                                bottom: '-1px',
                                left: '12%', right: '12%',
                                height: '2px',
                                bgcolor: D.accent,
                            }} />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

// ─── TierCard ───────────────────────────────────────────────────────────────
function TierCard({ tier, plan, features, recommended, isSelected, onSelect }) {
    const tierName = tier === 'pro' ? 'APEX USMLE PRO' : 'QBank';
    const tierTagline = tier === 'pro' ? 'всё включено' : 'тесты и задачи';

    return (
        <Box
            onClick={onSelect}
            role="button"
            tabIndex={0}
            sx={{
                cursor: 'pointer',
                position: 'relative',
                p: { xs: 2, sm: 2.5 },
                bgcolor: recommended ? D.bgRecommended : D.bgCard,
                border: `1px solid ${isSelected ? D.accent : D.rule}`,
                borderWidth: isSelected ? '1.5px' : '1px',
                borderRadius: '4px',
                transition: 'border-color 0.2s, transform 0.2s',
                '&:hover': { borderColor: D.accent },
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Eyebrow row: tagline + recommended badge */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ ...D.eyebrow, color: D.inkTertiary, fontSize: '0.62rem' }}>
                    {tierTagline}
                </Box>
                {recommended && (
                    <Box sx={{
                        ...D.eyebrow,
                        color: D.accent,
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        '&::before': {
                            content: '""',
                            display: 'inline-block',
                            width: 4, height: 4,
                            borderRadius: '50%',
                            bgcolor: D.accent,
                        },
                    }}>
                        Рекомендуем
                    </Box>
                )}
            </Stack>

            {/* Tier name */}
            <Typography sx={{
                fontFamily: D.serif,
                fontSize: { xs: '1.55rem', sm: '1.75rem' },
                fontWeight: 400,
                fontStyle: tier === 'pro' ? 'normal' : 'italic',
                color: D.ink,
                lineHeight: 1.05,
                mb: 1.5,
                letterSpacing: '-0.01em',
            }}>
                {tierName}
            </Typography>

            {/* Price */}
            <Typography sx={{
                fontFamily: D.serif,
                fontSize: { xs: '2.4rem', sm: '2.85rem' },
                fontWeight: 400,
                color: D.ink,
                lineHeight: 1,
                letterSpacing: '-0.025em',
                ...D.tabular,
            }}>
                {plan.price}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{
                    fontSize: '0.74rem',
                    color: D.inkTertiary,
                    textDecoration: 'line-through',
                    ...D.tabular,
                }}>
                    {plan.oldPrice}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: D.inkTertiary }}>·</Typography>
                <Typography sx={{
                    fontSize: '0.74rem',
                    color: D.inkSecondary,
                    ...D.tabular,
                }}>
                    {plan.perMonth}
                </Typography>
            </Stack>

            {/* Hairline */}
            <Box sx={{ borderTop: `0.5px dashed ${D.rule}`, mb: 1.5 }} />

            {/* Features */}
            <Stack spacing={0.85} sx={{ flex: 1 }}>
                {features.map(f => (
                    <Stack key={f.label} direction="row" spacing={1.25} alignItems="flex-start">
                        <Box sx={{
                            width: 14, minWidth: 14, mt: 0.25,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: f.in ? D.success : D.inkTertiary,
                            fontFamily: D.serif,
                            fontSize: f.in ? '1rem' : '0.85rem',
                            fontWeight: 500,
                            lineHeight: 1,
                        }}>
                            {f.in ? '✓' : '—'}
                        </Box>
                        <Typography sx={{
                            fontSize: '0.83rem',
                            color: f.in ? D.ink : D.inkTertiary,
                            opacity: f.in ? 1 : 0.7,
                            lineHeight: 1.35,
                        }}>
                            {f.label}
                        </Typography>
                    </Stack>
                ))}

                {plan.hasFreeze && (
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ pt: 0.5 }}>
                        <Snowflake size={14} weight="fill" color={D.accent} style={{ marginTop: 2, minWidth: 14 }} />
                        <Box>
                            <Typography sx={{
                                fontSize: '0.83rem',
                                fontWeight: 600,
                                color: D.accent,
                                lineHeight: 1.3,
                            }}>
                                Заморозка подписки
                            </Typography>
                            <Typography sx={{
                                fontSize: '0.72rem',
                                color: D.inkSecondary,
                                lineHeight: 1.3,
                            }}>
                                7-30 дней, один раз
                            </Typography>
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}

// ─── UpgradeModal ───────────────────────────────────────────────────────────
export default function UpgradeModal() {
    const { upgradeModalOpen, upgradeModalReason, closeUpgradeModal, token, trialInfo } =
        useContext(LayoutContext);

    const isTgPromo = trialInfo?.is_tg_promo === true;

    const [selectedTier, setSelectedTier] = useState('pro');     // 'pro' | 'qbank'
    const [selectedPeriod, setSelectedPeriod] = useState(12);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const findPlan = (plans, period) => plans.find(p => p.period === period);

    // QBank-сетка: для TG-юзера — спец-цена (TG_PROMO_PLANS),
    // для всех остальных — public (QBANK_PUBLIC_PLANS). Теперь обе доступны.
    const proPlan = isTgPromo
        ? findPlan(PRO_PROMO_PLANS, selectedPeriod)
        : findPlan(GRAND_SLAM_PLANS, selectedPeriod);
    const qbankPlan = isTgPromo
        ? findPlan(QBANK_PLANS, selectedPeriod)
        : findPlan(QBANK_PUBLIC_PLANS, selectedPeriod);
    const selectedPlan = selectedTier === 'qbank' ? qbankPlan : proPlan;

    const handlePay = async () => {
        if (!token) {
            setError('Сессия истекла, обновите страницу');
            return;
        }
        setLoading(true);
        setError('');
        try {
            // Маршрут платежа:
            //  - QBank + TG → APICreateTgPromoInvoice (старый flow)
            //  - QBank + не-TG → APICreateQbankInvoice (новый flow)
            //  - PRO (TG или regular) → APICreateGrandSlamInvoice
            let data;
            if (selectedTier === 'qbank') {
                data = isTgPromo
                    ? await APICreateTgPromoInvoice(token, selectedPeriod)
                    : await APICreateQbankInvoice(token, selectedPeriod);
            } else {
                data = await APICreateGrandSlamInvoice(token, selectedPeriod);
            }
            if (data?.payment_url) {
                window.location.href = data.payment_url;
                return;
            }
            setError(data?.error || 'Не удалось создать платёж');
        } catch (e) {
            console.error('Invoice error:', e);
            setError(e?.response?.data?.error || 'Не удалось создать платёж. Попробуйте ещё раз.');
        } finally {
            setLoading(false);
        }
    };

    // Иностранная карта — Prodamus invoice. Использует те же selectedPeriod/selectedTier,
    // но игнорирует QBank-only (Prodamus умеет только regular/PRO promo, не QBank).
    const handlePayForeign = async () => {
        if (!token) {
            setError('Сессия истекла, обновите страницу');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await APICreateProdamusInvoice(token, selectedPeriod);
            if (data?.payment_url) {
                window.location.href = data.payment_url;
                return;
            }
            setError(data?.error || 'Не удалось создать платёж');
        } catch (e) {
            console.error('Prodamus error:', e);
            setError(e?.response?.data?.error || 'Не удалось создать платёж. Попробуйте ещё раз.');
        } finally {
            setLoading(false);
        }
    };

    // Header copy logic
    const state = trialInfo?.state || 'active_trial';
    const daysLeft = trialInfo?.days_left ?? 0;

    let headerKicker = 'APEX · USMLE Step 1';
    let headerTitle, headerSub;

    if (isTgPromo) {
        headerKicker = 'Закрытый класс · Спец-цена';
        headerTitle = 'Открой полную программу';
        headerSub = upgradeModalReason ||
            'Выбери глубину доступа: только тесты и задачи или полный курс с модулями, видео и планами.';
    } else if (state === 'expired') {
        headerTitle = 'Триал закончился';
        headerSub = upgradeModalReason ||
            'Чтобы продолжить подготовку к Step 1, оформи полный доступ.';
    } else if (state === 'active_trial') {
        headerTitle = daysLeft > 0
            ? `Осталось ${daysLeft} ${daysLeft === 1 ? 'день' : daysLeft < 5 ? 'дня' : 'дней'} триала`
            : 'Последний день триала';
        headerSub = upgradeModalReason ||
            'Триал открывает первый день программы. Оплати, чтобы открыть весь курс.';
    } else {
        headerTitle = 'Полный доступ к APEX';
        headerSub = upgradeModalReason || 'Все материалы для подготовки к Step 1.';
    }

    return (
        <Modal open={!!upgradeModalOpen} onClose={closeUpgradeModal}>
            <ModalOverflow>
                <ModalDialog
                    sx={{
                        maxWidth: 760,
                        width: { xs: '95vw', sm: '92vw' },
                        bgcolor: D.bg,
                        backgroundImage: grainBg,
                        backgroundSize: '180px 180px',
                        p: { xs: 2, sm: 3.5 },
                        borderRadius: '6px',
                        border: `1px solid ${D.rule}`,
                    }}
                >
                    <ModalClose sx={{ color: D.inkSecondary }} />

                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 2.5, mt: 0.5 }}>
                        <Box sx={{ ...D.eyebrow, color: D.accent, mb: 1.5 }}>
                            {headerKicker}
                        </Box>
                        <Typography sx={{
                            fontFamily: D.serif,
                            fontSize: { xs: '1.7rem', sm: '2.15rem' },
                            fontWeight: 400,
                            color: D.ink,
                            lineHeight: 1.1,
                            letterSpacing: '-0.015em',
                            mb: 0.75,
                            maxWidth: 520,
                            mx: 'auto',
                        }}>
                            {headerTitle}
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.86rem',
                            color: D.inkSecondary,
                            lineHeight: 1.45,
                            maxWidth: 480,
                            mx: 'auto',
                        }}>
                            {headerSub}
                        </Typography>
                    </Box>

                    {/* Period selector */}
                    <PeriodSelector
                        selected={selectedPeriod}
                        onChange={setSelectedPeriod}
                    />

                    {/* Tier cards — обе колонки (PRO + QBank only) показываем всем,
                        backend сам подменяет цены TG vs non-TG. */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: { xs: 1.5, sm: 2 },
                        mb: 2,
                    }}>
                        <TierCard
                            tier="pro"
                            plan={proPlan}
                            features={PRO_FEATURES}
                            recommended
                            isSelected={selectedTier === 'pro'}
                            onSelect={() => setSelectedTier('pro')}
                        />
                        <TierCard
                            tier="qbank"
                            plan={qbankPlan}
                            features={QBANK_FEATURES}
                            recommended={false}
                            isSelected={selectedTier === 'qbank'}
                            onSelect={() => setSelectedTier('qbank')}
                        />
                    </Box>

                    {error && (
                        <Alert color="danger" sx={{ mb: 1.5, borderRadius: '4px' }}>
                            {error}
                        </Alert>
                    )}

                    {/* CTA */}
                    <Button
                        onClick={handlePay}
                        loading={loading}
                        sx={{
                            width: '100%',
                            bgcolor: D.ink,
                            color: D.bg,
                            fontFamily: 'inherit',
                            fontSize: '0.9rem',
                            letterSpacing: '0.03em',
                            fontWeight: 600,
                            py: 1.6,
                            borderRadius: '4px',
                            transition: 'background-color 0.2s, transform 0.15s',
                            '&:hover': {
                                bgcolor: '#000',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        Оплатить — {selectedPlan?.price}
                    </Button>

                    {/* Иностранная карта — Prodamus.
                        Не показываем для QBank tier (Prodamus принимает только regular/PRO promo
                        цены, QBank-only не поддерживается в `create_prodamus_invoice`). */}
                    {selectedTier !== 'qbank' && (
                        <Box sx={{ textAlign: 'center', mt: 1.25 }}>
                            <Box
                                component="button"
                                onClick={handlePayForeign}
                                disabled={loading}
                                sx={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: loading ? 'default' : 'pointer',
                                    color: D.inkSecondary,
                                    fontFamily: 'inherit',
                                    fontSize: '0.78rem',
                                    textDecoration: 'underline',
                                    textDecorationColor: D.rule,
                                    textUnderlineOffset: '3px',
                                    p: 0,
                                    '&:hover:not(:disabled)': {
                                        color: D.accent,
                                        textDecorationColor: D.accent,
                                    },
                                }}
                            >
                                Оплатить иностранной картой →
                            </Box>
                        </Box>
                    )}

                    {/* Footnote */}
                    <Stack direction="row" spacing={1.25} justifyContent="center" alignItems="center" sx={{ mt: 1.5 }}>
                        <Box sx={{
                            ...D.eyebrow,
                            color: D.inkTertiary,
                            fontSize: '0.6rem',
                        }}>
                            YooKassa · Prodamus
                        </Box>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: D.rule }} />
                        <Box sx={{
                            ...D.eyebrow,
                            color: D.inkTertiary,
                            fontSize: '0.6rem',
                        }}>
                            Не сдал Step 1 — возврат 100%
                        </Box>
                    </Stack>
                </ModalDialog>
            </ModalOverflow>
        </Modal>
    );
}
